import React, { useState } from 'react';
import { Package, CheckCircle, ArrowLeft, Shield, Truck, Plus, Minus, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UNIT_PRICE = 1500; // NOK incl. mva
const SHIPPING_FEE = 99; // NOK

export default function OrderDIYKitPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    phone: '',
    shipping_address: '',
    postal_code: '',
    city: '',
    quantity: 1,
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [orderResult, setOrderResult] = useState(null);

  const subtotal = formData.quantity * UNIT_PRICE;
  const total = subtotal + SHIPPING_FEE;

  const setQty = (n) => {
    setFormData(prev => ({ ...prev, quantity: Math.max(1, Math.min(50, n)) }));
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/diy-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setOrderResult(data);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError(data.detail || 'Kunne ikke sende bestilling. Prøv igjen.');
      }
    } catch {
      setError('Kunne ikke koble til serveren. Sjekk nettforbindelsen og prøv igjen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (orderResult) {
    return (
      <div className="min-h-screen bg-stone-50">
        <div className="bg-gradient-to-br from-emerald-900 to-emerald-700 text-white py-16">
          <div className="max-w-3xl mx-auto px-4">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-emerald-100 hover:text-white mb-6" data-testid="diy-back-home-btn">
              <ArrowLeft size={20} /> Tilbake til forsiden
            </button>
            <div className="flex items-center gap-4">
              <CheckCircle className="text-amber-300" size={56} />
              <div>
                <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Takk for bestillingen!</h1>
                <p className="text-emerald-100 mt-2">Ordrenummer: <span className="font-mono">{orderResult.id.slice(0, 8).toUpperCase()}</span></p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-12" data-testid="diy-order-success">
          <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">Hva skjer nå?</h2>
            <ol className="space-y-3 text-stone-700">
              <li className="flex gap-3"><span className="flex-shrink-0 w-7 h-7 bg-emerald-800 text-white rounded-full flex items-center justify-center text-sm font-semibold">1</span><span>Du mottar en bekreftelse på e-post (<strong>{orderResult.email}</strong>) med ordresammendrag.</span></li>
              <li className="flex gap-3"><span className="flex-shrink-0 w-7 h-7 bg-emerald-800 text-white rounded-full flex items-center justify-center text-sm font-semibold">2</span><span>Vi sender deg en faktura eller Vipps-krav på {orderResult.total_price} kr (inkl. mva og frakt).</span></li>
              <li className="flex gap-3"><span className="flex-shrink-0 w-7 h-7 bg-emerald-800 text-white rounded-full flex items-center justify-center text-sm font-semibold">3</span><span>Når betalingen er registrert, sendes kittet til adressen du oppga.</span></li>
              <li className="flex gap-3"><span className="flex-shrink-0 w-7 h-7 bg-emerald-800 text-white rounded-full flex items-center justify-center text-sm font-semibold">4</span><span>Hvert kit har et garantinummer + QR-kode. Skann QR-en med mobilen for å registrere din 10 års garanti etter påføring.</span></li>
            </ol>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h3 className="font-semibold text-stone-900 mb-2">Spørsmål?</h3>
            <p className="text-stone-700 text-sm">Bare svar på bekreftelses-eposten du nettopp fikk, så hjelper vi deg.</p>
          </div>

          <button onClick={() => navigate('/')} className="mt-8 bg-stone-900 text-white hover:bg-stone-800 rounded-full px-8 py-4 font-medium" data-testid="diy-order-home-btn">Tilbake til forsiden</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-gradient-to-br from-emerald-900 to-emerald-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-emerald-100 hover:text-white mb-6" data-testid="diy-back-home-btn">
            <ArrowLeft size={20} /> Tilbake til forsiden
          </button>
          <div className="flex items-center gap-4">
            <Package className="text-amber-300" size={48} />
            <div>
              <h1 className="text-3xl md:text-5xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Bestill gjør-det-selv kit</h1>
              <p className="text-emerald-100 text-lg mt-2">DC-Protect beskyttelse med 10 års garanti</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 grid lg:grid-cols-3 gap-8">
        {/* Left: kit info + form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">Hva inneholder kittet?</h2>
            <ul className="grid sm:grid-cols-2 gap-3 text-stone-700">
              {[
                '2 stk mikrofiberklut',
                '1 stk påføringssvamp',
                '1 flaske 20 ml forvask',
                '1 flaske DC-Protect 7,5 ml',
                '2 par hansker',
                'Garantinummer + QR-kode',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2"><CheckCircle2Like /> <span>{item}</span></li>
              ))}
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8 space-y-5" data-testid="diy-order-form">
            <h2 className="text-xl font-semibold text-stone-900">Dine opplysninger</h2>

            <Field label="Fullt navn *" required value={formData.customer_name} onChange={handleChange('customer_name')} testId="diy-name-input" />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="E-post *" type="email" required value={formData.email} onChange={handleChange('email')} testId="diy-email-input" />
              <Field label="Telefon *" type="tel" required value={formData.phone} onChange={handleChange('phone')} testId="diy-phone-input" />
            </div>
            <Field label="Leveringsadresse *" required value={formData.shipping_address} onChange={handleChange('shipping_address')} placeholder="Gate og nummer" testId="diy-address-input" />
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Postnr *" required value={formData.postal_code} onChange={handleChange('postal_code')} testId="diy-postal-input" />
              <div className="sm:col-span-2">
                <Field label="Sted *" required value={formData.city} onChange={handleChange('city')} testId="diy-city-input" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Antall kit *</label>
              <div className="flex items-center gap-3" data-testid="diy-qty-control">
                <button type="button" onClick={() => setQty(formData.quantity - 1)} className="w-12 h-12 rounded-full bg-stone-200 hover:bg-stone-300 flex items-center justify-center" aria-label="Reduser antall" data-testid="diy-qty-decrement"><Minus size={20} /></button>
                <input type="number" min="1" max="50" value={formData.quantity} onChange={(e) => setQty(parseInt(e.target.value || '1', 10))} className="w-20 text-center px-3 py-3 border border-stone-300 rounded-lg text-lg font-semibold" data-testid="diy-qty-input" />
                <button type="button" onClick={() => setQty(formData.quantity + 1)} className="w-12 h-12 rounded-full bg-emerald-800 text-white hover:bg-emerald-900 flex items-center justify-center" aria-label="Øk antall" data-testid="diy-qty-increment"><Plus size={20} /></button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Notat (valgfritt)</label>
              <textarea value={formData.notes} onChange={handleChange('notes')} rows={3} placeholder="Spesielle ønsker eller leveringsinformasjon" className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" data-testid="diy-notes-input" />
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="diy-order-error">
                <AlertTriangle size={18} /> <span>{error}</span>
              </div>
            )}

            <button type="submit" disabled={submitting} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 disabled:opacity-60 rounded-full px-8 py-4 text-lg font-medium transition-all" data-testid="diy-submit-btn">
              {submitting ? 'Sender bestilling...' : `Send bestilling — ${total} kr`}
            </button>
            <p className="text-center text-xs text-stone-500">Ingen betaling nå. Vi sender deg faktura eller Vipps-krav på e-post etter bestillingen.</p>
          </form>
        </div>

        {/* Right: order summary */}
        <aside className="lg:sticky lg:top-8 self-start">
          <div className="bg-stone-900 text-white rounded-xl shadow-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2"><Package size={20} /> Ordresammendrag</h3>
            <div className="space-y-2 text-sm border-t border-stone-700 pt-3">
              <div className="flex justify-between"><span>Pris pr. kit</span><span>{UNIT_PRICE} kr</span></div>
              <div className="flex justify-between"><span>Antall</span><span data-testid="diy-summary-qty">{formData.quantity}</span></div>
              <div className="flex justify-between"><span>Delsum</span><span>{subtotal} kr</span></div>
              <div className="flex justify-between"><span className="flex items-center gap-1"><Truck size={14} /> Frakt</span><span>{SHIPPING_FEE} kr</span></div>
            </div>
            <div className="flex justify-between items-baseline border-t border-stone-700 pt-3">
              <span className="text-stone-300">Totalt</span>
              <span className="text-2xl font-bold text-amber-300" data-testid="diy-summary-total">{total} kr</span>
            </div>
            <p className="text-xs text-stone-400">Alle priser er inkl. mva.</p>
            <div className="bg-emerald-900/40 border border-emerald-700 rounded-lg p-3 text-xs space-y-1">
              <div className="flex items-center gap-2 text-amber-300 font-medium"><Shield size={14} /> 10 års garanti</div>
              <p className="text-stone-200">Hvert kit inneholder garantinummer + QR-kode for enkel registrering.</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required = false, type = 'text', placeholder = '', testId = '' }) {
  return (
    <div>
      <label className="block text-sm font-medium text-stone-700 mb-2">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
        data-testid={testId}
      />
    </div>
  );
}

function CheckCircle2Like() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}
